/**
 * PURPOSE: Materializar el borrador en partidos reales (FTR-0028 WG3, §14; D-14, D-15).
 * RESPONSIBILITIES: Mostrar lo que va a pasar ANTES de que pase, confirmarlo y presentar el
 *   resultado real que devolvió el servidor.
 *
 *   NUNCA DESDE UN BOTÓN AMBIGUO. Materializar crea partidos, escribe `config_category` y la copia
 *   por evento de `fixture`: es la frontera entre diseñar y operar. Primero se pide el preview —que
 *   no escribe nada—, se muestra el resumen, y solo entonces se ejecuta.
 *
 *   ANTES DEL SORTEO ES LO NORMAL (D-14). Que un partido nazca sin participantes no es un defecto:
 *   es exactamente lo que permite publicar "A1 vs A4 · fecha · sede" antes de saber quién es A1. El
 *   panel lo cuenta como información, no como advertencia.
 *
 *   MATERIALIZAR NO OFICIALIZA (D-15). El partido nace en estado 1 y sin publicar; el resultado
 *   sigue siendo cosa del flujo de resultados.
 * COLLABORATORS: ProgrammingCenterProvider.jsx, services/programmingCenterService.js.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/panels/MaterializePanel.jsx
 */
import React, { useEffect, useState } from "react";
import { FiAlertTriangle, FiCheckCircle, FiCopy, FiDatabase } from "react-icons/fi";
import { toast } from "react-hot-toast";

import { useProgrammingCenter } from "../ProgrammingCenterProvider.jsx";
import { previewMaterialization } from "../../../../services/programmingCenterService.js";
import { programmingCenterErrorMessage } from "../../../../utils/programmingCenterErrors.js";
import SidePanel from "../components/SidePanel.jsx";

const Stat = ({ label, value, tone = "slate", hint = null }) => (
  <div
    className={`rounded-xl border px-3 py-2 ${
      tone === "green"
        ? "border-emerald-200 bg-emerald-50"
        : tone === "amber"
          ? "border-amber-200 bg-amber-50"
          : "border-slate-200 bg-slate-50"
    }`}
  >
    <p className="text-xl font-black text-slate-900">{value}</p>
    <p className="text-[11px] font-black uppercase tracking-wide text-slate-500">{label}</p>
    {hint ? <p className="mt-0.5 text-[11px] font-bold text-slate-400">{hint}</p> : null}
  </div>
);

const MaterializePanel = ({ open, onClose }) => {
  const { scope, actions, busy, summary } = useProgrammingCenter();
  const [plan, setPlan] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState({ writeConfigCategory: true, writeFixtureCopy: true });

  useEffect(() => {
    if (!open) {
      setPlan(null);
      setResult(null);
      return;
    }
    let active = true;
    setLoading(true);
    setResult(null);
    previewMaterialization(scope)
      .then((data) => {
        if (active) setPlan(data);
      })
      .catch((error) => {
        if (active) {
          setPlan(null);
          toast.error(programmingCenterErrorMessage(error, "Vista previa de materialización"));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [open, scope]);

  const run = async () => {
    const response = await actions.materialize(options);
    if (response.ok) {
      setResult(response.data);
      toast.success(
        `${response.data?.summary?.created ?? 0} partidos creados · ${
          response.data?.summary?.already ?? 0
        } ya existían`
      );
    }
  };

  const counts = plan?.counts || {};
  const disabled = loading || Boolean(busy);

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title="Materializar el fixture"
      subtitle="Convierte el borrador en partidos reales del evento"
    >
      {loading ? (
        <p className="text-sm font-bold text-slate-500">Calculando el impacto…</p>
      ) : !plan ? (
        <p className="text-sm font-bold text-slate-500">
          No se pudo calcular el plan de materialización.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat label="Se crearán" value={counts.create ?? 0} tone="green" />
            <Stat
              label="Se re-enlazan"
              value={counts.relink ?? 0}
              hint="Ya existían con la misma clave"
            />
            <Stat label="Ya materializados" value={counts.already ?? 0} />
            <Stat label="Filas del borrador" value={counts.rows ?? 0} />
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2">
            <Stat
              label="Lados sin participante"
              value={counts.pendingParticipants ?? 0}
              tone={counts.pendingParticipants ? "amber" : "slate"}
              hint="Normal antes del sorteo"
            />
            <Stat label="BYE en el fixture" value={summary.byes} />
          </div>

          {plan.ownership?.duplicates?.length ? (
            <section className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3">
              <p className="flex items-center gap-2 text-sm font-black text-amber-800">
                <FiCopy className="text-sm" />
                {plan.ownership.duplicates.length} partidos externos comparten coordenada
              </p>
              <p className="mt-1 text-xs font-bold text-amber-800">
                Existen partidos creados por otra herramienta en la misma posición estructural. El
                Centro no los toca; decidir cuál sobra es tuyo.
              </p>
              <ul className="mt-2 flex flex-col gap-1 text-xs font-bold text-amber-900">
                {plan.ownership.duplicates.slice(0, 8).map((duplicate) => (
                  <li key={duplicate.idmatch}>
                    Partido #{duplicate.nro_match || duplicate.idmatch} coincide con{" "}
                    {duplicate.collidesWith.join(", ")}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <section className="mt-4 flex flex-col gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
            <p className="text-[11px] font-black uppercase tracking-wide text-slate-500">
              Además de crear los partidos
            </p>
            <label className="flex items-start gap-2 text-sm font-bold text-slate-700">
              <input
                type="checkbox"
                checked={options.writeConfigCategory}
                onChange={(event) =>
                  setOptions((current) => ({
                    ...current,
                    writeConfigCategory: event.target.checked,
                  }))
                }
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400"
              />
              <span>
                Actualizar la configuración del deporte
                <span className="block text-[11px] font-bold text-slate-500">
                  Grupos, rondas, equipos por grupo y etapas finales. Configuración y SorteoSoft leen
                  esos campos.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm font-bold text-slate-700">
              <input
                type="checkbox"
                checked={options.writeFixtureCopy}
                onChange={(event) =>
                  setOptions((current) => ({ ...current, writeFixtureCopy: event.target.checked }))
                }
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400"
              />
              <span>
                Actualizar la copia de Fixture Base del evento
                <span className="block text-[11px] font-bold text-slate-500">
                  Idempotente: si ya coincide, no se escribe nada.
                </span>
              </span>
            </label>
          </section>

          <p className="mt-3 flex items-start gap-2 rounded-xl border border-blue-200 bg-blue-50 p-3 text-xs font-bold text-blue-800">
            <FiAlertTriangle className="mt-0.5 shrink-0 text-sm" />
            Materializar no oficializa nada: los partidos nacen habilitados y sin publicar. Los
            partidos que ya tengan actividad competitiva nunca se reescriben.
          </p>

          <button
            type="button"
            onClick={run}
            disabled={disabled}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-3 py-2.5 text-sm font-black text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FiDatabase className="text-sm" />
            Materializar {counts.create ?? 0} partidos
          </button>
        </>
      )}

      {result ? (
        <section className="mt-4 rounded-xl border border-emerald-300 bg-emerald-50 p-3">
          <p className="flex items-center gap-2 text-sm font-black text-emerald-800">
            <FiCheckCircle className="text-sm" />
            Materialización completada
          </p>
          <ul className="mt-2 flex flex-col gap-0.5 text-xs font-bold text-emerald-900">
            <li>{result.summary?.created ?? 0} partidos creados</li>
            <li>{result.summary?.relinked ?? 0} re-enlazados a partidos existentes</li>
            <li>{result.summary?.already ?? 0} ya estaban materializados</li>
            <li>{result.summary?.periods ?? 0} periodos creados</li>
            <li>{result.summary?.pendingParticipants ?? 0} lados aún sin participante</li>
          </ul>
        </section>
      ) : null}
    </SidePanel>
  );
};

export default MaterializePanel;
